import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation } from 'react-router';
import { loginRequestSchema, mfaVerifySchema, type LoginRequest, type SessionUser } from '@agency/contracts';
import { Button, Notice, TextField } from '@agency/ui';
import { ApiClientError, post } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { AuthCard } from './auth-card.tsx';

type LoginResponse = { user: SessionUser; csrfToken: string } | { mfaRequired: true; challenge: string };
type FormError = { message: string; requestId?: string };

export function LoginPage() {
  const { user, signedIn } = useAuth();
  const loc = useLocation();
  const [challenge, setChallenge] = useState<string | null>(null);
  if (user) return <Navigate to={(loc.state as { from?: string } | null)?.from ?? '/leads'} replace />;
  const finish = (r: LoginResponse) => {
    if ('mfaRequired' in r) setChallenge(r.challenge);
    else signedIn(r);
  };
  return challenge ? <MfaStep challenge={challenge} onDone={finish} onBack={() => setChallenge(null)} /> : <PasswordStep onDone={finish} />;
}

function PasswordStep({ onDone }: { onDone: (r: LoginResponse) => void }) {
  const [formError, setFormError] = useState<FormError | null>(null);
  const { register, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm<LoginRequest>({
    // Shared Zod contract is the single validator.
    resolver: async (values) => {
      const r = loginRequestSchema.safeParse(values);
      if (r.success) return { values: r.data, errors: {} };
      const errs: Record<string, { type: string; message: string }> = {};
      for (const i of r.error.issues) errs[String(i.path[0])] = { type: 'validation', message: i.path[0] === 'email' ? 'Enter your work email address.' : 'Enter your password.' };
      return { values: {}, errors: errs };
    },
  });
  return (
    <AuthCard title="Sign in" lead="Use your work account to continue.">
      {formError ? <Notice tone="error" title="Could not sign in">{formError.message}{formError.requestId ? ` (Reference ${formError.requestId})` : ''}</Notice> : null}
      <form noValidate onSubmit={handleSubmit(async (v) => {
        setFormError(null);
        try {
          onDone(await post<LoginResponse>('/auth/login', v));
        } catch (e) {
          const err = e instanceof ApiClientError ? e : null;
          if (err?.code === 'RATE_LIMITED') setFormError({ message: err.message, requestId: err.requestId });
          else if (err?.status === 401) setError('password', { message: 'Email or password is incorrect.' });
          else setFormError({ message: err?.message ?? 'Something went wrong. Try again.', requestId: err?.requestId });
        }
      })}>
        <TextField label="Email" type="email" autoComplete="username" required error={errors.email?.message} {...register('email')} />
        <TextField label="Password" type="password" autoComplete="current-password" required error={errors.password?.message} {...register('password')} />
        <Button type="submit" variant="primary" loading={isSubmitting}>Sign in</Button>
      </form>
      <p><Link to="/forgot-password">Forgot your password?</Link></p>
    </AuthCard>
  );
}

/** Second step: a 6-digit authenticator code, or a one-time recovery code. */
function MfaStep({ challenge, onDone, onBack }: { challenge: string; onDone: (r: LoginResponse) => void; onBack: () => void }) {
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthCard title="Two-step verification" lead={recovery ? 'Enter one of your recovery codes. Each works once.' : 'Enter the 6-digit code from your authenticator app.'}>
      {error ? <Notice tone="error" title="That didn’t work">{error}</Notice> : null}
      <form noValidate onSubmit={async (e) => {
        e.preventDefault();
        const parsed = mfaVerifySchema.safeParse({ challenge, code });
        if (!parsed.success) { setError(recovery ? 'Recovery codes look like abcde-12345.' : 'Enter the 6-digit code.'); return; }
        setBusy(true); setError(null);
        try {
          onDone(await post<LoginResponse>('/auth/mfa/verify', parsed.data));
        } catch (err) {
          const ce = err instanceof ApiClientError ? err : null;
          if (ce?.status === 401 && /expired/i.test(ce.message)) { onBack(); return; }
          setError(ce?.message ?? 'Something went wrong. Try again.');
        } finally { setBusy(false); }
      }}>
        <TextField
          label={recovery ? 'Recovery code' : 'Authentication code'}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode={recovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          autoFocus
          required
          hint={recovery ? undefined : 'The code changes every 30 seconds.'}
        />
        <Button type="submit" variant="primary" loading={busy}>Verify</Button>
      </form>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <button type="button" className="ag-btn ag-btn--ghost ag-btn--sm" onClick={() => { setRecovery(!recovery); setCode(''); setError(null); }}>{recovery ? 'Use authenticator code' : 'Use a recovery code'}</button>
        <button type="button" className="ag-btn ag-btn--ghost ag-btn--sm" onClick={onBack}>Back to sign in</button>
      </div>
    </AuthCard>
  );
}
